import { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useRouter from 'utils/useRouter'
import { useLocal } from 'hooks/useLocal'
import { listarCidadesRN } from 'actions'
import axios from 'axios'

export const useFormLocal = (props) => {
  const { local, veiculo, setLocalAtualizado, idCaso, setOpenFicha, openFicha } = props

  const dispatch = useDispatch()
  const router = useRouter()
  const localService = useLocal()
  const endereco = useSelector((state) => state.mapa)

  const [values, setValues] = useState({
    logradouro: '',
    numero: '',
    bairro: '',
    complemento: '',
    latitude: '',
    longitude: '',
    municipio: null,
    cep: '',
    estado: 'RN',
  })

  const [cidades, setCidades] = useState([])
  const [processando, setProcessando] = useState(false)
  const [openAlertDelete, setOpenAlertDelete] = useState(false)
  const [openMirror, setOpenMirror] = useState(false)
  const [cep, setCep] = useState('')
  const [errors, setErrors] = useState({})

  // Carregar cidades do RN
  useEffect(() => {
    dispatch(listarCidadesRN(setCidades))
  }, [dispatch])

  // Preencher form quando o local for carregado/editado
  useEffect(() => {
    if (local && local.id > 0) {

      setValues({
        logradouro: local.rua || '',
        numero: local.numero || '',
        bairro: local.bairro || '',
        complemento: local.complemento || '',
        latitude: local.latitudeGps || '',
        longitude: local.longitudeGps || '',
        municipio: local.cidade ? { nome: local.cidade } : null,
        cep: local.cep || '',
        estado: local.estado || 'RN',
      })
      if (local.cep) {
        setCep(local.cep)
      }
    }
  }, [local])

  // Tratar localização do mapa do redux
  useEffect(() => {
    if (endereco.dados && endereco.dados[0]) {
      const dadosEndereco = endereco.dados[0].address
      const municipio = dadosEndereco.city || dadosEndereco.city_district || dadosEndereco.town || dadosEndereco.village

      setValues((prev) => ({
        ...prev,
        logradouro: dadosEndereco.road || prev.logradouro || '',
        numero: prev.numero || '',
        municipio: municipio ? { nome: municipio } : prev.municipio || null,
        bairro: dadosEndereco.suburb || prev.bairro || '',
        latitude: endereco.dados[0].lat || prev.latitude || '',
        longitude: endereco.dados[0].lon || prev.longitude || '',
        estado: dadosEndereco.state_code || prev.estado || 'RN',
      }))
    }
  }, [endereco])

  // Limpar formulário se fechar/abrir a ficha
  useEffect(() => {
    if (openFicha !== undefined) {
      setValues({
        logradouro: '',
        numero: '',
        bairro: '',
        complemento: '',
        latitude: '',
        longitude: '',
        municipio: null,
        cep: '',
        estado: 'RN',
      })
      setErrors({})
    }
  }, [openFicha])

  // Validação simples em tempo real
  const validate = useCallback((currentValues) => {
    const tempErrors = {}
    if (!currentValues.municipio || !currentValues.municipio.nome) {
      tempErrors.municipio = 'Cidade é obrigatória'
    }
    if (!currentValues.logradouro || !currentValues.logradouro.trim()) {
      tempErrors.logradouro = 'Logradouro é obrigatório'
    }
    if (!currentValues.bairro || !currentValues.bairro.trim()) {
      tempErrors.bairro = 'Bairro é obrigatório'
    }
    setErrors(tempErrors)
    return Object.keys(tempErrors).length === 0
  }, [])

  useEffect(() => {
    validate(values)
  }, [values, validate])

  const isFormValid = Object.keys(errors).length === 0

  const handleCepChange = (event) => {
    setCep(event.target.value.replace(/^(\d{5})(\d)/, '$1-$2'))
  }

  const handleSearchCep = useCallback(async () => {
    if (!cep) return
    setProcessando(true)
    try {
      const formattedCep = cep.replace('-', '')
      const response = await axios.get(`https://viacep.com.br/ws/${formattedCep}/json/`)
      const addressData = response.data
      if (addressData && !addressData.erro) {
        setValues((prev) => ({
          ...prev,
          cep: addressData.cep || cep,
          logradouro: addressData.logradouro || '',
          municipio: addressData.localidade ? { nome: addressData.localidade } : null,
          bairro: addressData.bairro || '',
          complemento: addressData.complemento || '',
          estado: addressData.uf || 'RN',
        }))
      }
    } catch (error) {
      console.error('Error fetching CEP data:', error)
    } finally {
      setProcessando(false)
    }
  }, [cep])

  const handleChange = useCallback((event) => {
    const { name, value } = event.target
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }))
  }, [])

  const handleAutocompleteChange = useCallback((name, newValue) => {
    setValues((prev) => ({
      ...prev,
      [name]: newValue,
    }))
  }, [])

  const handleConfirm = useCallback(async () => {
    if (!isFormValid) return
    const isEditMode = local && local.id > 0
    setProcessando(true)
    try {
      if (isEditMode) {
        await localService.editarLocal(local.id, values, setProcessando)
        if (veiculo) {
          if (setLocalAtualizado) setLocalAtualizado(values)
          setOpenMirror(false)
          return
        }
        router.history.push(`/casos/caso/detalhar/${local.id_caso}`)
      } else {
        await localService.cadastrarLocal(values, idCaso, setProcessando)
        if (setOpenFicha) {
          setOpenFicha(false)
        } else {
          router.history.push(`/casos/caso/detalhar/${idCaso}`)
        }
      }
    } catch (error) {
      console.error('Erro ao salvar local:', error)
    } finally {
      setProcessando(false)
      setOpenMirror(false)
    }
  }, [local, values, idCaso, veiculo, setLocalAtualizado, setOpenFicha, router, isFormValid])

  const handleDelete = useCallback(async () => {
    if (!local || !local.id) return
    setProcessando(true)
    try {
      await localService.excluirLocal(local.id, setProcessando)
      setOpenAlertDelete(false)
      router.history.push(`/casos/caso/detalhar/${local.id_caso}`)
    } catch (error) {
      console.error('Erro ao excluir local:', error)
    } finally {
      setProcessando(false)
    }
  }, [local, router])

  const handleCancelar = useCallback(() => {
    if (setOpenFicha) {
      setOpenFicha(false)
    }
  }, [setOpenFicha])

  const handleCoordinateInput = useCallback((event) => {
    const value = event.target.value
    // Remove todos os caracteres que não são números, ponto (.), vírgula (,) e sinal de menos (-)
    const newValue = value.replace(/[^-0-9.,]/g, '')
    // Certifique-se de que o sinal de menos (-) só aparece no início
    const sanitizedValue = newValue.replace(/-+/g, '-')
    // Substitua vírgulas por pontos para tratar decimais
    const commaToDotValue = sanitizedValue.replace(/,/g, '.')
    // Verifique se há mais de um ponto decimal e remova extras, exceto o primeiro
    const finalValue = commaToDotValue.replace(/(\..*)\./g, '$1')

    event.target.value = finalValue
  }, [])

  return {
    values,
    cidades,
    processando,
    openAlertDelete,
    setOpenAlertDelete,
    openMirror,
    setOpenMirror,
    cep,
    errors,
    isFormValid,
    handleCepChange,
    handleSearchCep,
    handleChange,
    handleAutocompleteChange,
    handleConfirm,
    handleDelete,
    handleCancelar,
    handleCoordinateInput,
    router,
  }
}
